import { memo, useRef, Fragment } from "react";
import { Button, Row, Col } from "reactstrap";
import { Popover, Whisper, WhisperInstance } from "rsuite";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../../App";
import classnames from "classnames";
import useSortableFields from "../../../hooks/useSortableFields";
import useFieldMap from "../../../hooks/useFieldMap";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import StrUtils from "../../../utils/StrUtils";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import { QueryOrderBy } from "../../../types/GlobalInterfaces";

interface Props {
  savedSortBy?: QueryOrderBy;
  sortBy?: QueryOrderBy;
  updateSortBy: (newOrderBy: QueryOrderBy | null) => void;
  moduleAPIName: string | undefined;
  onSave?: (sortBy: QueryOrderBy | null) => void;
  custom?:
    | {
        APIName: string;
        Name: string;
        FieldType: string;
        Protected: true;
        ReadOnly: true;
        order: QueryOrderBy | null | string;
        sortFunction: (field: string) => void;
      }[]
    | null; // Subitem Junk
  createMode?: boolean;
}

const SortBy = ({
  savedSortBy,
  sortBy,
  updateSortBy,
  moduleAPIName,
  onSave,
  custom = null,
  createMode = false,
}: Props) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const sortByWhisperRef = useRef<WhisperInstance>(null);
  const fieldNameMap = useFieldMap(moduleAPIName, "Name");
  const { sortableFields, loadingFields } = useSortableFields(moduleAPIName);

  const customIcon = custom
    ? custom.filter((field) => field.order !== "")
    : null;

  // Options for the sort by dropdown menu
  const SortbyOptions = () => {
    return (
      <PopoverDropdownMenu>
        <div className="px-2 pt-2 ">
          <Row className="align-items-center">
            <Col xs="">
              <h5 className="m-0 text-dark">
                <i className="mdi mdi-swap-vertical pr-2"></i>Sort Records By
              </h5>
            </Col>
            {onSave && (
              <Col xs="auto">
                <Button
                  size="sm"
                  outline
                  color="primary"
                  disabled={
                    !onSave ||
                    (savedSortBy?.Field === sortBy?.Field &&
                      savedSortBy?.Direction === sortBy?.Direction)
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onSave(sortBy || null);
                  }}
                >
                  {savedSortBy?.Field === sortBy?.Field &&
                  savedSortBy?.Direction === sortBy?.Direction ? (
                    <>Default</>
                  ) : (
                    "Save As Default"
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </div>
        <DropdownMenuDivider />
        <div
          style={{
            maxHeight: "300px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {custom &&
            custom.map((field) => (
              <Fragment key={field.APIName}>
                <DropdownOptionWithIcon
                  name={field.Name}
                  onClick={(e) => {
                    field.sortFunction(field.APIName);
                  }}
                  icon={StrUtils.iconForField(field as any)}
                  active={sortBy?.Field === field.APIName}
                  // @ts-ignore
                  actionIcon={
                    sortBy?.Field === field.APIName
                      ? sortBy?.Direction === "DESC"
                        ? "mdi mdi-sort-descending"
                        : "mdi mdi-sort-ascending"
                      : ""
                  }
                ></DropdownOptionWithIcon>
              </Fragment>
            ))}
          {!custom ? (
            sortableFields?.length > 0 ? (
              sortableFields?.map((field) => (
                <Fragment key={field.APIName}>
                  <DropdownOptionWithIcon
                    name={field.Name}
                    onClick={(e) => {
                      if (
                        sortBy?.Field === field.APIName &&
                        sortBy?.Direction === "ASC"
                      ) {
                        return updateSortBy(null);
                      }

                      updateSortBy({
                        Field: field.APIName,
                        Direction:
                          sortBy?.Field === field.APIName ? "ASC" : "DESC",
                      });
                    }}
                    icon={StrUtils.iconForField(field)}
                    active={sortBy?.Field === field.APIName}
                    // @ts-ignore
                    actionIcon={
                      sortBy?.Field === field.APIName
                        ? sortBy?.Direction === "DESC"
                          ? "mdi mdi-sort-descending"
                          : "mdi mdi-sort-ascending"
                        : ""
                    }
                    isDefault={savedSortBy?.Field === field.APIName}
                  ></DropdownOptionWithIcon>
                </Fragment>
              ))
            ) : (
              <div className="py-2 pl-4 font-weight-500">
                No Sortable Fields Available.
              </div>
            )
          ) : null}
        </div>
      </PopoverDropdownMenu>
    );
  };

  return (
    <Whisper
      placement="autoVertical"
      trigger="click"
      ref={sortByWhisperRef}
      preventOverflow
      speaker={
        <Popover
          className="sidebar-popover"
          style={{ minWidth: "350px" }}
          full
          onClick={(e) => {
            e.stopPropagation();
            //sortByWhisperRef.current.close();
          }}
        >
          <SortbyOptions />
        </Popover>
      }
    >
      {mobileScreen ? (
        <Button
          style={{ height: 36, width: 32 }}
          outline
          disabled={createMode}
          className="d-flex justify-content-center align-items-center m-0 p-0"
        >
          <i className="mdi mdi-swap-vertical" style={{ fontSize: 32 }} />
        </Button>
      ) : (
        <Button
          size="sm"
          color="secondary"
          className={
            "d-flex align-items-center justify-content-between text-truncate m-0"
          }
          disabled={createMode}
          outline={!sortBy}
          style={{
            maxWidth: 200,
            gap: "10px",
          }}
        >
          {sortBy ? (
            <>
              <i
                className={classnames(
                  "m-0 p-0",
                  { "mdi mdi-sort-ascending": sortBy?.Direction === "ASC" },
                  { "mdi mdi-sort-descending": sortBy?.Direction === "DESC" },
                  { "mdi mdi-swap-vertical": !sortBy }
                )}
                style={{
                  fontSize: 16,
                }}
              ></i>
              <span>
                {loadingFields
                  ? "Loading..."
                  : `By ${fieldNameMap[sortBy?.Field]}`}
              </span>
              <i
                className="mdi mdi-chevron-down m-0 p-0"
                style={{ fontSize: "20px" }}
              />
            </>
          ) : (
            <>
              {custom && sortBy ? (
                <>
                  <i
                    className={classnames(
                      "m-0 p-0",

                      {
                        "mdi mdi-sort-ascending":
                          // @ts-ignore
                          customIcon?.[0]?.Direction === "asc",
                      },
                      // @ts-ignore
                      {
                        "mdi mdi-sort-descending":
                          // @ts-ignore
                          customIcon[0]?.Direction === "desc",
                      },
                      // @ts-ignore
                      { "mdi mdi-swap-vertical": !customIcon[0] }
                    )}
                    style={{
                      fontSize: 16,
                    }}
                  />
                  {/* @ts-ignore */}
                  <span>{customIcon[0] ? customIcon[0].Name : "Sort"}</span>
                  <i
                    className="mdi mdi-chevron-down m-0 p-0"
                    style={{ fontSize: "20px" }}
                  />
                </>
              ) : (
                <>
                  <i
                    className={classnames("mdi mdi-swap-vertical m-0 p-0")}
                    style={{
                      fontSize: 16,
                    }}
                  />
                  <span>Sort</span>
                  <i
                    className="mdi mdi-chevron-down m-0 p-0"
                    style={{ fontSize: "20px" }}
                  />
                </>
              )}
            </>
          )}
        </Button>
      )}
    </Whisper>
  );
};

export default memo(SortBy);
